import { useCallback, useContext } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  Dividend,
  DividendCollection,
} from "wealthmode-frontend/backend"
import { Box, HStack } from "wealthmode-frontend/gluestack/ui"
import { formatRecurringAmount } from "wealthmode-frontend/util"

import { DataTable } from "@/components"
import { BackendContext } from "@/context"

export default function Dividends() {
  const { client_id, account_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
  }>()

  const backend = useContext(BackendContext)

  const load = useCallback(async () => {
    const clients = backend.collection("clients") as ClientCollection
    const client = clients.model(client_id)
    await client.fetch({ reload: true })

    const accounts = client.link("accounts")?.resolve() as AccountCollection
    const account = accounts.model(account_id)
    await account.fetch({ reload: true })

    const data = account.embed("data") as AccountData
    const dividends = data.link("dividends")?.resolve() as DividendCollection | undefined
    if (!dividends) {
      return undefined
    }

    await dividends.fetch({ reload: true })

    return dividends
  }, [backend, client_id, account_id])

  return (
    <Box>
      <HStack>
        <Box className="flex-1">
          <DataTable
            title={"Dividends"}
            load={load}
            header={[
              { id: "id", title: "ID", shrink: true },
              { id: "instrument", title: "Instrument" },
              { id: "frequency", title: "Frequency" },
              { id: "interval", title: "Interval", shrink: true },
              { id: "amount", title: "Amount" },
              { id: "reinvested", title: "Reinvested", shrink: true },
            ]}
            render={(model: Dividend, field: string) => {
              switch (field) {
                case "instrument":
                  const sec = model.instrument()
                  if (sec == null) {
                    return null
                  }
                  return (
                    <Link href={`/instruments/${sec.attributes.id}`}>
                      {sec.attributes.symbol ?? (sec.attributes.type == "cash" ? "Cash" : sec.attributes.type)}
                    </Link>
                  )

                // Dividends are only detected for holdings-based recurrences, so amount is
                // always a per-unit rate, never a payment total.
                case "amount":
                  return formatRecurringAmount({ amount_type: "holdings", amount: model.get(field) })

                case "reinvested":
                  return model.attributes.reinvested ? "Yes" : "No"
              }
              return model.get(field)
            }}
          />
        </Box>
      </HStack>
    </Box>
  )
}
